import React, { useState, useContext } from 'react';
import { SessionContext } from '@/app/providers/SessionProvider';
import toast from 'react-hot-toast';
import { useClient, useMutation } from 'jsonapi-react';
import { FiThumbsUp, FiMessageCircle, FiTrash2 } from 'react-icons/fi';
import Swal from 'sweetalert2';
import api from '@/app/services/api.js';
import getPermission from '@/app/utils/getPermission';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { MdOutlinePushPin, MdPushPin } from 'react-icons/md';
import getAuthorityLevel from '@/app/utils/getAuthorityLevel';
import { cn } from '@/lib/utils';

const PostActionsWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-post-actions-wrapper',
      // Estilos Base
      'flex w-4 flex-col gap-3',
      className
    )}
    {...props}
  />
));
PostActionsWrapper.displayName = 'PostActionsWrapper';

const PostActionsItem = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-post-actions-item',
      // Estilos Base
      'inline-flex flex-col text-center',
      className
    )}
    {...props}
  />
));
PostActionsItem.displayName = 'PostActionsItem';

const PostActionsButton = React.forwardRef(({ className, isFilled, isDanger, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'post-actions-button',
      // Estilos Base
      'mx-auto inline-flex w-4 cursor-pointer text-primary transition-transform duration-200 ease-in-out',
      'hover:scale-125',
      // Variantes
      isFilled && '[&_svg]:fill-primary-light',
      isDanger && 'text-danger',
      className
    )}
    {...props}
  />
));
PostActionsButton.displayName = 'PostActionsButton';

const PostActionsCount = React.forwardRef(({ className, isVisible, ...props }, ref) => {
  return (
    isVisible && (
      <p
        ref={ref}
        className={cn(
          'post-actions-count',
          // Estilos de Fonte e Cor
          'mb-0 text-xs !font-extrabold text-primary-light',
          // Margem e Animação
          'animate-fade-in duration-500',
          className
        )}
        {...props}
      />
    )
  );
});
PostActionsCount.displayName = 'PostActionsCount';

function PostActions({ setPosts, deletePost, post: postParam, isDeleting }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [post, setPost] = useState(postParam);
  const client = useClient();
  const [addUserInteraction] = useMutation('user_interactions');
  const state = useContext(SessionContext);
  const authorityLevel = getAuthorityLevel();
  const likeUnlikePost = e => {
    e.preventDefault();
    e.stopPropagation();
    if (post['like-id'] === null) {
      addUserInteraction({
        action: 'liked',
        'interactionable-type': 'Post',
        'interactionable-id': post.id
      })
        .then()
        .then(response => {
          if (response.error) {
            toast.error(response.error.title);
          } else {
            let newPost = post;
            newPost['likes-count'] += 1;
            newPost['like-id'] = response.data.id;
            setPost({ ...newPost });
          }
        })
        .catch(error => {
          toast.error(t('toast.error'));
          console.error(error);
        });
    } else {
      api
        .delete(`user_interactions/${post['like-id']}`)
        .then(response => {
          let newPost = post;
          newPost['likes-count'] -= 1;
          newPost['like-id'] = null;
          setPost({ ...newPost });
        })
        .catch(error => {
          console.error(error);
        });
    }
  };

  const handleFixPost = async event => {
    event.stopPropagation();

    const { data, error } = await client.mutate(`posts/${post.id}`, {
      pinned: post.pinned ? false : true
    });

    if (error) {
      toast.error('Erro ao fixar postagem');
      return;
    }

    setPost(prevState => {
      return {
        ...prevState,
        pinned: post.pinned ? false : true
      };
    });

    if (!post.pinned) {
      setPosts(prev => [data, ...prev.filter(item => item.id !== data.id)]);
    }
  };

  return (
    <PostActionsWrapper>
      <Tooltip id="fixed-post" />
      {authorityLevel !== 'student' && (
        <PostActionsItem>
          <PostActionsButton
            className="w-5"
            onClick={handleFixPost}
          >
            {post.pinned ? (
              <MdPushPin
                size={20}
                data-tooltip-id="fixed-post"
                data-tooltip-content="Desafixar"
              />
            ) : (
              <MdOutlinePushPin
                size={20}
                data-tooltip-id="fixed-post"
                data-tooltip-content="Fixar"
              />
            )}
          </PostActionsButton>
        </PostActionsItem>
      )}

      {state.session && state.session.user.id === post?.['user-id'] && (
        <PostActionsItem>
          <PostActionsButton
            isDanger
            onClick={event => {
              event.stopPropagation();
              Swal.fire({
                title: t('projects.warningDeleteCommentTitle'),
                text: t('projects.warningDeleteCannotBeUndoneText'),
                confirmButtonText: t('button.yes'),
                showDenyButton: true,
                denyButtonText: t('button.no'),
                showCloseButton: true
              }).then(res => {
                if (res.isConfirmed) {
                  deletePost(post);
                }
              });
            }}
            disabled={isDeleting}
          >
            <FiTrash2 />
          </PostActionsButton>
        </PostActionsItem>
      )}

      <PostActionsItem onClick={e => likeUnlikePost(e)}>
        <PostActionsButton isFilled={post['like-id'] !== null}>
          <FiThumbsUp />
        </PostActionsButton>

        <PostActionsCount isVisible={post['likes-count'] > 0}>{post['likes-count']}</PostActionsCount>
      </PostActionsItem>

      {getPermission('Visualizar postagens e comentários', 'Fórum') && (
        <PostActionsItem
          onClick={event => {
            event.stopPropagation();
            navigate(`/posts/${post.id}`);
          }}
        >
          <PostActionsButton>
            <FiMessageCircle />
          </PostActionsButton>

          <PostActionsCount isVisible={post['comments-count'] > 0}>{post['comments-count']}</PostActionsCount>
        </PostActionsItem>
      )}
    </PostActionsWrapper>
  );
}

export { PostActions, PostActionsWrapper, PostActionsItem, PostActionsButton, PostActionsCount };
